import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Portal,
} from '@mui/material';
import {
  Home as HomeIcon,
  ListAlt as AllOrdersIcon,
  // LocalShipping removed (delivery merged into drivers)
  PointOfSale as CashDeskIcon,
  TableBar as RestaurantIcon,
  SoupKitchen as KitchenIcon,
  DirectionsCar as DriverIcon,
  Assessment as ReportsIcon,
  BarChart as BackofficeIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
  Logout as LogoutIcon,
  ChevronLeft,
  ChevronRight,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useInstantTheme } from '../contexts/InstantThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import FirebaseAuthService from '../services/firebaseAuthService';
import ShiftCashDialog from './ShiftCashDialog';

interface SharedSidebarProps {
  children?: React.ReactNode;
  onCollapseChange?: (collapsed: boolean) => void;
  mobileMode?: boolean;
  onMobileClose?: () => void;
}

const SharedSidebar: React.FC<SharedSidebarProps> = ({ onCollapseChange, mobileMode, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appTheme } = useInstantTheme();
  const { language, setLanguage, t } = useLanguage();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [languageMenuPosition, setLanguageMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const handleToggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    localStorage.setItem('sidebar_collapsed', String(newCollapsed));
    if (onCollapseChange) {
      onCollapseChange(newCollapsed);
    }
    window.dispatchEvent(new CustomEvent('sidebar-collapse-change'));
  };

  // Admin check — only admins can see Settings
  const ADMIN_EMAILS = ['admin@saheehpos.com', 'admin@saheehpos.com', 'owner@saheehpos.com'];
  const getEmail = (): string => {
    // 1. Firebase Auth (most reliable when available)
    const fbEmail = FirebaseAuthService.getInstance().getCurrentUser()?.email;
    if (fbEmail) return fbEmail.toLowerCase();
    // 2. localStorage saheeh_pos_user
    try {
      const u = JSON.parse(localStorage.getItem('saheeh_pos_user') || '{}');
      if (u.email) return u.email.toLowerCase();
    } catch {}
    // 3. localStorage saheeh_pos_session
    try {
      const s = JSON.parse(localStorage.getItem('saheeh_pos_session') || '{}');
      if (s.user?.email) return s.user.email.toLowerCase();
    } catch {}
    return '';
  };
  const detectedEmail = getEmail();
  const isAdmin = ADMIN_EMAILS.includes(detectedEmail);
  console.log(`🛡️ [SIDEBAR] email="${detectedEmail}" isAdmin=${isAdmin}`);

  // Liefersoft-style menu items
  const menuItems = [
    { path: '/dashboard', label: 'Home', translationKey: 'nav.dashboard', icon: <HomeIcon /> },
    { path: '/orders', label: 'All orders', translationKey: 'nav.orders', icon: <AllOrdersIcon /> },
    // Delivery merged into /drivers page
    { path: '/restaurant', label: 'Cash desk', translationKey: 'nav.restaurant', icon: <CashDeskIcon /> },
    { path: '/tables', label: 'Restaurant', translationKey: 'nav.tables', icon: <RestaurantIcon /> },
    { path: '/kitchen', label: 'Kitchen screen', translationKey: 'nav.kitchen', icon: <KitchenIcon /> },
    { path: '/drivers', label: 'Driver screen', translationKey: 'nav.drivers', icon: <DriverIcon /> },
    { path: '/daily-closing', label: 'Daily Closing', translationKey: 'nav.reports', icon: <ReportsIcon /> },
    { path: '/backoffice', label: 'Backoffice', translationKey: 'nav.backoffice', icon: <BackofficeIcon /> },
    // Settings only visible to admin users
    ...(isAdmin ? [{ path: '/settings', label: 'Settings', translationKey: 'nav.settings', icon: <SettingsIcon /> }] : []),
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/' || location.pathname === '/dashboard';
    if (path === '/daily-closing') return location.pathname === '/daily-closing' || location.pathname === '/reports';
    if (path === '/restaurant') return location.pathname === '/restaurant' || location.pathname === '/payments';
    if (path === '/settings') return location.pathname.startsWith('/settings');
    return location.pathname === path;
  };

  // FRAUD PREVENTION #7: Cash reconciliation on logout
  const [shiftCloseOpen, setShiftCloseOpen] = React.useState(false);
  const [shiftSummary, setShiftSummary] = React.useState<any>(null);
  const [closingLoading, setClosingLoading] = React.useState(false);

  const performLogout = async () => {
    try {
      const authService = FirebaseAuthService.getInstance();
      await authService.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('saheeh_pos_session');
      localStorage.removeItem('saheeh_pos_user');
      localStorage.removeItem('saheeh_pos_shift_id');
      localStorage.removeItem('saheeh_pos_opening_cash_asked');
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    const shiftId = localStorage.getItem('saheeh_pos_shift_id');
    if (!shiftId) {
      // No active shift — just logout
      await performLogout();
      return;
    }
    // Fetch the current shift summary to show what cash should be in drawer
    try {
      const { getFirestore, doc, getDoc, collection, query, where, getDocs } = await import('../services/firestoreCompat');
      const db = getFirestore();
      const shiftSnap = await getDoc(doc(db, 'pos_shifts', shiftId));
      if (shiftSnap.exists()) {
        const shift = shiftSnap.data();
        // Quick aggregate from orders since shift start
        const startTime = shift.start_time?.toDate?.() || new Date();
        let totalCash = 0, totalSales = 0, ordersCount = 0, refundsAmount = 0;
        try {
          const ordSnap = await getDocs(query(
            collection(db, 'orders'),
            where('created_by_email', '==', shift.user_email)
          ));
          ordSnap.forEach(d => {
            const o = d.data();
            const created = o.created_at?.toDate?.() || new Date(o.created_at || 0);
            if (created < startTime) return;
            const status = (o.status || '').toLowerCase();
            const total = parseFloat(o.total || '0');
            if (status === 'completed' || status === 'delivered' || o.payment_status === 'completed') {
              ordersCount++;
              totalSales += total;
              if ((o.payment_method || '').toLowerCase() === 'cash') totalCash += total;
            }
            if (o.refund_amount) refundsAmount += parseFloat(o.refund_amount || 0);
          });
        } catch { /* ignore */ }
        const expected = (shift.cash_opening || 0) + totalCash - refundsAmount;
        setShiftSummary({
          cash_opening: shift.cash_opening || 0,
          total_cash: totalCash,
          refunds_amount: refundsAmount,
          cash_expected: Math.round(expected * 100) / 100,
          orders_count: ordersCount,
          total_sales: Math.round(totalSales * 100) / 100,
        });
      }
    } catch (e) {
      console.warn('Could not load shift summary:', e);
    }
    setShiftCloseOpen(true);
  };

  const handleCashCloseConfirm = async (cashAmount: number) => {
    setClosingLoading(true);
    try {
      const shiftId = localStorage.getItem('saheeh_pos_shift_id');
      const { HAS_VALID_FIREBASE_CONFIG } = await import('../services/firebaseConfig');
      if (shiftId && HAS_VALID_FIREBASE_CONFIG) {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const closeShift = httpsCallable(getFunctions(), 'closeShift');
        // If the cashier bypassed the cash count (NaN sentinel from the
        // "close without counting" confirm), send null so the cloud function
        // stores cash_closing: null and cash_variance: null — this makes the
        // uncounted close show up clearly in the admin shift report.
        const payload: any = { shift_id: shiftId };
        payload.closing_cash = (typeof cashAmount === 'number' && !isNaN(cashAmount)) ? cashAmount : null;
        await closeShift(payload);
        localStorage.removeItem('saheeh_pos_shift_id');
      } else if (shiftId) {
        // No Firebase Functions wired — just clear local state.
        localStorage.removeItem('saheeh_pos_shift_id');
      }
      setShiftCloseOpen(false);
      await performLogout();
    } catch (err) {
      console.error('Close shift failed:', err);
      // Logout anyway
      await performLogout();
    } finally {
      setClosingLoading(false);
    }
  };

  // Prevent the cashier from escaping the shift close by closing the browser
  // tab / refreshing the page. The native beforeunload dialog will prompt
  // "Are you sure you want to leave?" and on OK the shift stays open — that's
  // fine, the next login resumes the same shift (openShift is idempotent).
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const shiftId = localStorage.getItem('saheeh_pos_shift_id');
      if (!shiftId) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const effectiveCollapsed = mobileMode ? false : collapsed;
  const sidebarWidth = effectiveCollapsed ? 72 : 240;
  const accentColor = appTheme.theme.primary_color || '#00C896';

  const handleNavClick = (path: string) => {
    navigate(path);
    if (mobileMode && onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <Box
      sx={{
        width: mobileMode ? '100%' : sidebarWidth,
        backgroundColor: '#0A2540',
        display: 'flex',
        flexDirection: 'column',
        position: mobileMode ? 'relative' : 'fixed',
        left: mobileMode ? undefined : 0,
        top: mobileMode ? undefined : 0,
        height: '100vh',
        zIndex: 1000,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'width 0.2s ease',
        borderRight: mobileMode ? 'none' : '1px solid #1e2139',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#2a2d3e',
          borderRadius: 2,
        },
      }}
    >
      {/* Logo / Brand Section */}
      <Box sx={{
        px: effectiveCollapsed ? 1.5 : 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: effectiveCollapsed ? 0 : 1.2,
        minHeight: 68,
        borderBottom: '1px solid #1e2139',
      }}>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
          <img
            src="/saheeh_logo_w.svg"
            alt="Saheeh"
            style={{ width: effectiveCollapsed ? 46 : 168, height: effectiveCollapsed ? 46 : 'auto', objectFit: 'contain' }}
          />
        </Box>
        {/* Collapse toggle - hide in mobile mode */}
        {!mobileMode && (
        <IconButton
          onClick={handleToggleCollapse}
          size="small"
          sx={{
            marginLeft: effectiveCollapsed ? 0 : 'auto',
            color: '#6b7280 !important',
            width: 28,
            height: 28,
            '&:hover': {
              color: '#ffffff !important',
              backgroundColor: 'rgba(255,255,255,0.05) !important',
            },
          }}
        >
          {effectiveCollapsed ? <ChevronRight sx={{ fontSize: 18 }} /> : <ChevronLeft sx={{ fontSize: 18 }} />}
        </IconButton>
        )}
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, py: 1, px: effectiveCollapsed ? 0.5 : 1 }}>
        {menuItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Tooltip title={effectiveCollapsed ? (t(item.translationKey) || item.label) : ''} placement="right" key={item.path}>
              <Button
                fullWidth
                onClick={() => handleNavClick(item.path)}
                sx={{
                  justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                  textTransform: 'none',
                  mb: 0.25,
                  py: 1.2,
                  px: effectiveCollapsed ? 1 : 2,
                  borderRadius: '8px',
                  minWidth: effectiveCollapsed ? 48 : 'auto',
                  minHeight: 42,
                  backgroundColor: active
                    ? 'rgba(0, 200, 150, 0.12) !important'
                    : 'transparent !important',
                  color: active
                    ? `${accentColor} !important`
                    : '#9ca3af !important',
                  borderLeft: active ? `3px solid ${accentColor}` : '3px solid transparent',
                  '&:hover': {
                    backgroundColor: active
                      ? 'rgba(0, 200, 150, 0.15) !important'
                      : 'rgba(255, 255, 255, 0.05) !important',
                    color: active ? `${accentColor} !important` : '#e0e0e0 !important',
                    '& .MuiSvgIcon-root': {
                      color: active ? `${accentColor} !important` : '#e0e0e0 !important',
                    },
                  },
                  fontWeight: active ? 600 : 400,
                  fontSize: '0.875rem',
                  gap: effectiveCollapsed ? 0 : 1.5,
                  '& .MuiButton-startIcon': {
                    margin: effectiveCollapsed ? 0 : undefined,
                    marginRight: effectiveCollapsed ? 0 : 0,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: 20,
                    color: active ? `${accentColor} !important` : '#6b7280 !important',
                  },
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: effectiveCollapsed ? 0 : 1.5,
                  width: '100%',
                }}>
                  {item.icon}
                  {!effectiveCollapsed && (
                    <Typography sx={{
                      fontSize: '0.875rem',
                      fontWeight: active ? 600 : 400,
                      color: 'inherit !important',
                      whiteSpace: 'nowrap',
                    }}>
                      {t(item.translationKey) || item.label}
                    </Typography>
                  )}
                </Box>
              </Button>
            </Tooltip>
          );
        })}
      </Box>

      {/* Bottom Section */}
      <Box sx={{ borderTop: '1px solid #1e2139', p: effectiveCollapsed ? 0.5 : 1 }}>
        {/* Help */}
        <Tooltip title={effectiveCollapsed ? 'Help' : ''} placement="right">
          <Button
            fullWidth
            onClick={() => {/* Help action */}}
            sx={{
              justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
              textTransform: 'none',
              py: 1,
              px: effectiveCollapsed ? 1 : 2,
              borderRadius: '8px',
              minWidth: effectiveCollapsed ? 48 : 'auto',
              backgroundColor: 'transparent !important',
              color: '#6b7280 !important',
              gap: effectiveCollapsed ? 0 : 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
                color: '#e0e0e0 !important',
              },
              '& .MuiSvgIcon-root': {
                fontSize: 20,
                color: '#6b7280 !important',
              },
            }}
          >
            <HelpIcon />
            {!effectiveCollapsed && (
              <Typography sx={{ fontSize: '0.875rem', color: 'inherit !important' }}>
                Help
              </Typography>
            )}
          </Button>
        </Tooltip>

        {/* Language Switcher */}
        <Button
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            if (languageMenuAnchor) {
              setLanguageMenuAnchor(null);
              setLanguageMenuPosition(null);
            } else {
              const rect = e.currentTarget.getBoundingClientRect();
              setLanguageMenuAnchor(e.currentTarget);
              setLanguageMenuPosition({ top: rect.top, left: rect.left });
            }
          }}
          sx={{
            justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
            textTransform: 'none',
            py: 1,
            px: effectiveCollapsed ? 1 : 2,
            borderRadius: '8px',
            minWidth: effectiveCollapsed ? 48 : 'auto',
            backgroundColor: 'transparent !important',
            color: '#6b7280 !important',
            gap: effectiveCollapsed ? 0 : 1.5,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
              color: '#e0e0e0 !important',
            },
            '& .MuiSvgIcon-root': {
              fontSize: 20,
              color: '#6b7280 !important',
            },
          }}
        >
          <LanguageIcon />
          {!effectiveCollapsed && (
            <Typography sx={{ fontSize: '0.875rem', color: 'inherit !important' }}>
              {language === 'en' ? '🇬🇧 EN' : '🇩🇪 DE'}
            </Typography>
          )}
        </Button>

        {/* Language Dropdown */}
        {languageMenuAnchor && languageMenuPosition && (
          <Portal>
            <Box
              sx={{
                position: 'fixed',
                bottom: window.innerHeight - languageMenuPosition.top + 4,
                left: `${languageMenuPosition.left}px`,
                width: 180,
                zIndex: 1300,
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                border: '1px solid #3a3d52',
                backgroundColor: '#252838 !important',
                p: 0.5,
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {[
                { code: 'en', label: '🇬🇧 English' },
                { code: 'ar', label: '🇸🇦 العربية' },
              ].map(({ code, label }) => (
                <Box
                  key={code}
                  onClick={() => {
                    setLanguage(code as 'en' | 'ar');
                    setLanguageMenuAnchor(null);
                    setLanguageMenuPosition(null);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.2,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: language === code
                      ? `${accentColor} !important`
                      : 'transparent !important',
                    color: language === code ? '#1a1d2e !important' : '#e0e0e0 !important',
                    fontWeight: language === code ? 600 : 400,
                    '&:hover': {
                      backgroundColor: language === code
                        ? `${accentColor} !important`
                        : 'rgba(255, 255, 255, 0.08) !important',
                    },
                  }}
                >
                  <Typography sx={{
                    fontWeight: language === code ? 600 : 400,
                    fontSize: '0.875rem',
                    color: 'inherit !important',
                  }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Portal>
        )}

        {/* Logout */}
        <Tooltip title={effectiveCollapsed ? t('nav.logout') : ''} placement="right">
          <Button
            fullWidth
            onClick={handleLogout}
            sx={{
              justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
              textTransform: 'none',
              py: 1,
              px: effectiveCollapsed ? 1 : 2,
              mt: 0.5,
              borderRadius: '8px',
              minWidth: effectiveCollapsed ? 48 : 'auto',
              backgroundColor: 'transparent !important',
              color: '#ef4444 !important',
              gap: effectiveCollapsed ? 0 : 1.5,
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1) !important',
              },
              '& .MuiSvgIcon-root': {
                fontSize: 20,
                color: '#ef4444 !important',
              },
            }}
          >
            <LogoutIcon />
            {!effectiveCollapsed && (
              <Typography sx={{ fontSize: '0.875rem', color: 'inherit !important' }}>
                {t('nav.logout')}
              </Typography>
            )}
          </Button>
        </Tooltip>
      </Box>

      {/* FRAUD PREVENTION #7: Cash reconciliation dialog on logout.
          onClose requires an explicit confirm — we refuse to let staff bypass
          the cash count by clicking Cancel or the backdrop. Either count the
          drawer and submit, or confirm that you're leaving without counting
          (which gets logged as variance=null in the shift doc). */}
      <ShiftCashDialog
        open={shiftCloseOpen}
        mode="close"
        summary={shiftSummary}
        loading={closingLoading}
        onConfirm={handleCashCloseConfirm}
        onClose={() => {
          const confirmBypass = window.confirm(
            'Are you sure you want to close your shift WITHOUT counting the drawer?\n\n' +
            'This will be logged in the shift record as an uncounted close and flagged ' +
            'in the admin report. Only do this in an emergency.\n\n' +
            'Click OK to logout anyway, or Cancel to go back and count the cash.'
          );
          if (!confirmBypass) return; // keep the dialog open
          // User chose to bypass — close shift with null cash (will be flagged)
          handleCashCloseConfirm(NaN as any);
        }}
      />
    </Box>
  );
};

export default SharedSidebar;
